import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertBody, AlertDescription, AlertIcon, AlertTitle } from '../../../design-system/alert';
import { useCurrentCompany } from '../../../lib/who-am-i';
import { useUserCountMetadata } from '../../../queries/users';
import { GroupType } from '../../../schemas/groups';
import { ClaimLimitPeriod } from '../../../schemas/incentives';

type BudgetEstimateEmployeeProps = {
	amount: number;
	conditions: { attr: string | undefined; value: string | undefined }[];
	operator: GroupType;
	period: ClaimLimitPeriod;
	userClaimLimit: number | null;
};

export const BudgetEstimateEmployee: FC<BudgetEstimateEmployeeProps> = ({ amount, conditions, operator, period, userClaimLimit }) => {
	const { t } = useTranslation('incentives', { keyPrefix: 'form' });
	const company = useCurrentCompany();

	const filteredConditions = useMemo(() => {
		return conditions.filter((condition) => condition.attr && condition.value) as { attr: string; value: string }[];
	}, [conditions]);

	const { data } = useUserCountMetadata({ conditions: filteredConditions, operator, status: 'active' });

	if (!data || !amount || !userClaimLimit) {
		return null;
	}

	const totalUsers = data.scopedCount;
	const totalPoints = totalUsers * userClaimLimit * amount;
	const totalDollars = Math.trunc(totalPoints / company.budget.exchange_rate);
	const companyCurrency = company.budget?.iso_code || 'USD';
	const key1 = [
		'estimatePerEmployee',
		totalUsers > 1 ? 'usersPlural' : 'userSingular',
		userClaimLimit > 1 ? 'quantityPlural' : 'quantitySingular',
		period,
	].join('_');

	return (
		<Alert status="info" size="sm">
			<AlertIcon />
			<AlertBody>
				<AlertTitle>
					{t('estimateTitle', {
						value: totalDollars,
						currency: companyCurrency,
						maximumFractionDigits: 0,
						context: period,
					})}
				</AlertTitle>
				<AlertDescription>
					{t(key1, { users: totalUsers, quantity: userClaimLimit })} {t('estimateTotal', { count: totalPoints, context: period })}
				</AlertDescription>
			</AlertBody>
		</Alert>
	);
};
